import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #dragController = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setupMobileDrag();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
      this.#dragController?.abort();
  this.#dragController = null;
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };
#setupMobileDrag() {
  this.#dragController?.abort();

  const dialog = this.#dialog;
  if (!dialog) return;

  const controller = new AbortController();
  this.#dragController = controller;

  const options = { signal: controller.signal };
  const mobile = window.matchMedia('(max-width: 749px)');

  let pointerId = null;
  let startY = 0;
  let distance = 0;
  let originalTranslate = '';

  const reset = () => {
    const capturedId = pointerId;
    pointerId = null;
    dialog.style.translate = originalTranslate;

    if (
      capturedId !== null &&
      dialog.hasPointerCapture(capturedId)
    ) {
      dialog.releasePointerCapture(capturedId);
    }
  };

  const finish = (event, cancelled = false) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    const shouldClose =
      !cancelled && mobile.matches && distance >= 70;
    if (shouldClose) {
      const drawer = this.#themeDrawer;
      if (!drawer) {
        reset();
        return;
      }

      const capturedId = pointerId;
      pointerId = null;

      if (
        capturedId !== null &&
        dialog.hasPointerCapture(capturedId)
      ) {
        dialog.releasePointerCapture(capturedId);
      }

      drawer.close().finally(() => {
        dialog.style.translate = originalTranslate;
      });

      return;
    }

    reset();

  };

  dialog.addEventListener('pointerdown', (event) => {
    if (!mobile.matches) return;
    if (!event.isPrimary || event.button !== 0) return;
    if (pointerId !== null) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.cart-drawer__drag-handle')) return;

    pointerId = event.pointerId;
    startY = event.clientY;
    distance = 0;
    originalTranslate = dialog.style.translate;

    dialog.setPointerCapture(pointerId);
  }, options);

  dialog.addEventListener('pointermove', (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    if (!mobile.matches) {
      reset();
      return;
    }

    distance = Math.max(0, event.clientY - startY);
    dialog.style.translate = `0 ${distance}px`;
  }, options);

  dialog.addEventListener('pointerup', (event) => {
    finish(event);
  }, options);

  dialog.addEventListener('pointercancel', (event) => {
    finish(event, true);
  }, options);

  dialog.addEventListener('lostpointercapture', (event) => {
    finish(event, true);
  }, options);

  mobile.addEventListener('change', () => {
    if (!mobile.matches && pointerId !== null) reset();
  }, options);

  dialog.addEventListener('close', () => {
    if (pointerId !== null) reset();
  }, options);

  controller.signal.addEventListener('abort', () => {
    if (pointerId !== null) reset();
  }, { once: true });
}

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
